import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  X, Search, BadgeCheck, MapPin, UserPlus, UserCheck, Users, Loader2, Sparkles,
} from "lucide-react";

/**
 * Followers / Following modal.
 * Props:
 *   userId: string — whose connections to show
 *   userName?: string — for the heading ("Maya Chen's network")
 *   initialTab?: "followers" | "following"
 *   onClose: () => void
 *   onCountChange?: ({ followers, following }) => void   // bubble up live counts
 */
export default function FollowersModal({
  userId, userName = "", initialTab = "followers", onClose, onCountChange,
}) {
  const { user: viewer } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(initialTab);
  const [followers, setFollowers] = useState(null); // null = loading
  const [following, setFollowing] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [busyMap, setBusyMap] = useState({}); // {id: bool}

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onEsc); };
  }, [onClose]);

  const load = async () => {
    setErr("");
    try {
      const [fRes, gRes] = await Promise.all([
        api.get(`/users/${userId}/followers?limit=200`),
        api.get(`/users/${userId}/following?limit=200`),
      ]);
      setFollowers(fRes.data || []);
      setFollowing(gRes.data || []);
      onCountChange?.({ followers: (fRes.data||[]).length, following: (gRes.data||[]).length });
    } catch (e) { setErr(formatErr(e)); }
  };

  useEffect(() => { load(); }, [userId]);

  const rows = useMemo(() => {
    const list = (tab === "followers" ? followers : following) || [];
    const ql = q.trim().toLowerCase();
    if (!ql) return list;
    return list.filter(u =>
      (u.name || "").toLowerCase().includes(ql) ||
      (u.location || "").toLowerCase().includes(ql) ||
      ((u.categories || []).join(" ") + " " + (u.category || "")).toLowerCase().includes(ql)
    );
  }, [tab, q, followers, following]);

  const goToProfile = (id) => { onClose(); nav(`/u/${id}`); };

  const toggleFollow = async (row) => {
    if (!viewer) { onClose(); nav("/login"); return; }
    if (row.is_self) return;
    setBusyMap(m => ({ ...m, [row.id]: true }));
    try {
      if (row.is_following) {
        await api.delete(`/follow/${row.id}`);
      } else {
        await api.post(`/follow/${row.id}`);
      }
      // Update both lists in place
      const flip = (list) => (list || []).map(u =>
        u.id === row.id
          ? { ...u, is_following: !row.is_following, follower_count: Math.max(0, (u.follower_count || 0) + (row.is_following ? -1 : 1)) }
          : u);
      setFollowers(flip);
      setFollowing(flip);
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusyMap(m => ({ ...m, [row.id]: false })); }
  };

  return createPortal((
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
      data-testid="followers-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 pt-5 pb-3">
          <div>
            <p className="overline">{userName ? `${userName.split(" ")[0]}'s network` : "Network"}</p>
            <h3 className="font-heading text-2xl mt-0.5">People</h3>
          </div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-[#F2F2F0]" aria-label="Close" data-testid="followers-modal-close">
            <X size={18}/>
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 sm:px-6 border-b border-[#E5E5E3]">
          <div className="flex gap-1">
            <Tab
              active={tab === "followers"}
              onClick={() => setTab("followers")}
              count={followers?.length}
              icon={<Users size={14}/>}
              label="Followers"
              testid="tab-followers"
            />
            <Tab
              active={tab === "following"}
              onClick={() => setTab("following")}
              count={following?.length}
              icon={<UserCheck size={14}/>}
              label="Following"
              testid="tab-following"
            />
          </div>
        </div>

        {/* Search */}
        <div className="px-5 sm:px-6 pt-4">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]"/>
            <input
              className="input pl-9"
              placeholder={`Search ${tab}…`}
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              data-testid="followers-search"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-2 sm:px-3 py-3" data-testid="followers-list">
          {err && <p className="text-sm text-[#B84B4B] px-4 py-2">{err}</p>}
          {(followers === null || following === null) && (
            <div className="text-center py-12 text-[#8A8A8A]"><Loader2 size={16} className="animate-spin inline mr-2"/>Loading…</div>
          )}
          {followers !== null && rows.length === 0 && (
            <div className="text-center py-12" data-testid="followers-empty">
              <Sparkles size={20} className="mx-auto text-[#0057FF] mb-2"/>
              <p className="text-sm text-[#4A4A4A]">
                {q
                  ? `No matches for "${q}"`
                  : tab === "followers"
                    ? "No followers yet — share the profile to grow the network."
                    : "Not following anyone yet."}
              </p>
            </div>
          )}

          {rows.map(r => (
            <Row
              key={r.id}
              row={r}
              busy={!!busyMap[r.id]}
              onOpen={() => goToProfile(r.id)}
              onToggle={() => toggleFollow(r)}
            />
          ))}
        </div>
      </div>
    </div>
  ), document.body);
}

function Tab({ active, onClick, count, icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors
        ${active ? "text-[#0A0A0A]" : "text-[#8A8A8A] hover:text-[#4A4A4A]"}`}
    >
      {icon}
      {label}
      {typeof count === "number" && (
        <span className={`text-xs ${active ? "text-[#0A0A0A]" : "text-[#8A8A8A]"}`}>· {count}</span>
      )}
      {active && <span className="absolute left-3 right-3 bottom-0 h-0.5 bg-[#0A0A0A] rounded-full"/>}
    </button>
  );
}

function Row({ row, busy, onOpen, onToggle }) {
  const cats = (row.categories?.length ? row.categories : [row.category]).filter(Boolean);
  const btnLabel = row.is_following
    ? "Following"
    : row.follows_you
      ? "Follow back"
      : "Follow";

  return (
    <div
      className="group flex items-center gap-3 rounded-2xl px-3 py-2.5 hover:bg-[#F2F2F0] transition-colors cursor-pointer"
      onClick={onOpen}
      data-testid={`follower-row-${row.id}`}
    >
      <img
        src={row.avatar}
        alt={row.name}
        className="h-12 w-12 rounded-full object-cover bg-[#E5E5E3] shrink-0"
        data-testid={`follower-avatar-${row.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="font-medium truncate" data-testid={`follower-name-${row.id}`}>{row.name}</p>
          {row.is_verified && <BadgeCheck size={14} className="text-[#0A0A0A] shrink-0"/>}
          {row.follows_you && !row.is_self && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#F2F2F0] text-[#4A4A4A] border border-[#E5E5E3]"
                  data-testid={`follower-followsyou-${row.id}`}>
              Follows you
            </span>
          )}
        </div>
        <p className="text-xs text-[#8A8A8A] truncate">
          {cats.slice(0, 2).join(" · ") || "—"}
          {row.location && <> <span className="opacity-60">·</span> <MapPin size={10} className="inline -mt-0.5"/> {row.location}</>}
        </p>
      </div>

      {!row.is_self && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          disabled={busy}
          data-testid={`follower-action-${row.id}`}
          className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3.5 py-2 border transition-all
            ${row.is_following
              ? "bg-white text-[#0A0A0A] border-[#E5E5E3] hover:bg-[#F2F2F0]"
              : row.follows_you
                ? "bg-[#0A0A0A] text-white border-[#0A0A0A] hover:bg-[#1F1F1F]"
                : "bg-[#0A0A0A] text-white border-[#0A0A0A] hover:bg-[#1F1F1F]"}`}
        >
          {busy
            ? <Loader2 size={12} className="animate-spin"/>
            : row.is_following ? <UserCheck size={12}/> : <UserPlus size={12}/>}
          {btnLabel}
        </button>
      )}
      {row.is_self && (
        <span className="shrink-0 text-xs text-[#8A8A8A] px-2 py-1" data-testid={`follower-self-${row.id}`}>You</span>
      )}
    </div>
  );
}
